<div class="drawer-content" [class.embedded]="embedded">
    <div *ngIf="loading" class="flex-center loading-pane">
        <mat-spinner diameter="40"></mat-spinner>
    </div>

    <div *ngIf="!loading" class="fhd-flex-wrapper">
        <div *ngIf="activeCase" class="fhd-flex-wrapper gap-16">
            <mat-card appearance="outlined" class="active-case-card">
                <mat-card-header>
                    <mat-icon mat-card-avatar color="primary">assignment</mat-icon>
                    <mat-card-title>{{'@caseNumber' | translate}}: {{activeCase.orderNumber ||
                        ('@pendingOrderNumberAssignment' | translate)}}</mat-card-title>
                    <mat-card-subtitle>{{'@assetName' | translate}}: {{activeCase.assetName}}</mat-card-subtitle>
                </mat-card-header>
                <mat-card-content [class.form-loading]="submitting">
                    <p><strong>{{'@reasonForCase' | translate}}:</strong> {{activeCase.reasonForWork}}</p>
                    <div class="flex-container assignment-row">
                        <div class="assignment-field flex-1">
                            <span class="field-caption">{{ '@assignedUser' | translate }}</span>
                            <app-filter-dropdown data-e2e="case-assignee-select" [searchable]="true" [selectOnExactMatch]="true"
                                [label]="'@none' | translate" [allLabel]="'@none' | translate"
                                [options]="assigneeOptions" [disabled]="!canEdit"
                                [(ngModel)]="activeCase.assignedUserId">
                            </app-filter-dropdown>
                        </div>
                        <div class="assignment-field flex-1">
                            <span class="field-caption">{{ '@assignedLocation' | translate }}</span>
                            <app-filter-dropdown data-e2e="case-location-select" [searchable]="true" [selectOnExactMatch]="true"
                                [label]="'@none' | translate" [allLabel]="'@none' | translate"
                                [options]="locationOptions" [disabled]="!canEdit"
                                [(ngModel)]="activeCase.assignedLocationId">
                            </app-filter-dropdown>
                        </div>
                    </div>

                    <!-- No [allLabel]: a status is required, so the picker must not offer a None row. -->
                    <div class="assignment-field">
                        <span class="field-caption">{{ '@status' | translate }}</span>
                        <app-filter-dropdown data-e2e="case-status-select" [searchable]="true"
                            [label]="'@status' | translate"
                            [options]="statusOptions" [disabled]="!canEdit"
                            [(ngModel)]="updateModel.userStatusId">
                        </app-filter-dropdown>
                    </div>
                </mat-card-content>
            </mat-card>
            <div class="chat-panel">
                <div class="chat-panel-header">
                    <span class="chat-panel-title">{{ '@history' | translate }}</span>
                    <button mat-icon-button aria-label="Copy chat history" [matTooltip]="'@copyChat' | translate"
                        (click)="copyChat()">
                        <mat-icon>content_copy</mat-icon>
                    </button>
                </div>

                <div class="chat-scroll-area" #chatScrollArea>
                    <div *ngFor="let history of combinedHistory; trackBy: trackHistory" class="comment-bubble"
                        [ngClass]="{'current-status-bubble': history.isCurrent, 'mine': history.userId === userId, 'user-comment': history.isComment}">
                        <div class="comment-header">
                            <span class="comment-avatar" *ngIf="history.isComment">{{history.authorInitials}}</span>
                            <span class="comment-user">{{history.userName || history.userId || ('@system' |
                                translate)}}</span>
                            <span class="comment-kind" *ngIf="history.isComment">{{'@caseComment' | translate}}</span>
                            <span class="comment-date">{{history.createDateTimeUtc | date:'short'}}</span>
                            <button *ngIf="canDeleteComment(history)" mat-icon-button class="comment-delete-btn"
                                [disabled]="submittingComment" [matTooltip]="'@caseDeleteComment' | translate"
                                (click)="deleteComment(history)">
                                <mat-icon>delete_outline</mat-icon>
                            </button>
                        </div>
                        <div class="comment-body">
                            <p class="comment-text">{{history.message || ('@noMessageProvided' | translate)}}</p>
                        </div>
                    </div>
                </div>

                <!-- Single note input: same look as the pre-11566 status-message box, but it now
                     writes a Case comment (POST case/{id}/comments). Users cannot write free text
                     into a status change — status transitions carry only the auto audit note. -->
                <div class="chat-input-row" *ngIf="canEdit">
                    <mat-form-field appearance="outline" class="chat-input-field" subscriptSizing="dynamic">
                        <mat-label>{{ '@message' | translate }}</mat-label>
                        <textarea matInput data-e2e="case-comment-input" cdkTextareaAutosize cdkAutosizeMinRows="1" cdkAutosizeMaxRows="4"
                            [(ngModel)]="commentDraft"
                            placeholder="{{ '@enterUpdateMessage' | translate }}"
                            (keydown.enter)="onCommentEnter($event)"></textarea>
                    </mat-form-field>
                    <button mat-icon-button aria-label="Add comment" class="chat-send-btn" data-e2e="case-comment-send"
                        [disabled]="!commentDraft.trim() || submittingComment" (click)="addComment()"
                        [matTooltip]="'@caseAddComment' | translate">
                        <mat-spinner diameter="20" *ngIf="submittingComment"></mat-spinner>
                        <mat-icon *ngIf="!submittingComment">send</mat-icon>
                    </button>
                </div>
            </div>
        </div>

        <div *ngIf="!activeCase && !canEdit" class="fhd-flex-wrapper gap-16">
            <mat-card appearance="outlined" class="active-case-card no-case-card">
                <mat-card-header>
                    <mat-icon mat-card-avatar color="primary">lock</mat-icon>
                    <mat-card-title>{{ '@noCaseExists' | translate }}</mat-card-title>
                </mat-card-header>
            </mat-card>
        </div>

        <div *ngIf="!activeCase && canEdit" class="fhd-flex-wrapper gap-16">
            <mat-card appearance="outlined" class="active-case-card create-case-card">
                <mat-card-header>
                    <mat-icon mat-card-avatar color="primary">construction</mat-icon>
                    <mat-card-title>{{ '@createCase' | translate }}</mat-card-title>
                    <mat-card-subtitle>{{'@createCaseDescription' | translate}}</mat-card-subtitle>
                </mat-card-header>
                <mat-card-content [class.form-loading]="submitting">
                    <div class="flex-container assignment-row">
                        <div class="assignment-field flex-1">
                            <span class="field-caption">{{ '@assignedUser' | translate }}</span>
                            <app-filter-dropdown data-e2e="case-assignee-select" [searchable]="true" [selectOnExactMatch]="true"
                                [label]="'@none' | translate" [allLabel]="'@none' | translate"
                                [options]="assigneeOptions" [(ngModel)]="createModel.assignedUserId">
                            </app-filter-dropdown>
                        </div>
                        <div class="assignment-field flex-1">
                            <span class="field-caption">{{ '@assignedLocation' | translate }}</span>
                            <app-filter-dropdown data-e2e="case-location-select" [searchable]="true" [selectOnExactMatch]="true"
                                [label]="'@none' | translate" [allLabel]="'@none' | translate"
                                [options]="locationOptions" [(ngModel)]="createModel.assignedLocationId">
                            </app-filter-dropdown>
                        </div>
                    </div>

                    <div class="assignment-field">
                        <span class="field-caption">{{ '@status' | translate }}</span>
                        <app-filter-dropdown data-e2e="case-status-select" [searchable]="true"
                            [label]="'@status' | translate"
                            [options]="statusOptions"
                            [(ngModel)]="updateModel.userStatusId">
                        </app-filter-dropdown>
                    </div>

                    <!-- Optional opening note. It is created as the case's first comment
                         (authored by the creator), not written into the status. -->
                    <mat-form-field appearance="fill">
                        <mat-label>{{ '@message' | translate }}</mat-label>
                        <textarea matInput data-e2e="case-opening-comment-input" cdkTextareaAutosize
                            cdkAutosizeMinRows="2" cdkAutosizeMaxRows="5" [(ngModel)]="createModel.comment"
                            placeholder="{{ '@enterUpdateMessage' | translate }}"></textarea>
                    </mat-form-field>
                </mat-card-content>
            </mat-card>
        </div>
    </div>
</div>

<div class="fhd-button-group fhd-border-group footer-actions" [class.embedded]="embedded">
    <button *ngIf="!embedded" mat-raised-button (click)="closeClicked()">{{ '@close' | translate }}</button>
    <div class="flex-spacer"></div>
    <button *ngIf="activeCase && canEdit" mat-raised-button color="primary" [disabled]="!hasChanges || submitting"
        (click)="updateCase()">
        <mat-spinner diameter="20" *ngIf="submitting" class="btn-spinner"></mat-spinner>
        {{ '@update' | translate }}
    </button>
    <button *ngIf="!activeCase && !loading && canEdit" mat-raised-button color="primary" [disabled]="submitting"
        (click)="save()">
        <mat-spinner diameter="20" *ngIf="submitting" class="btn-spinner"></mat-spinner>
        {{ '@save' | translate }}
    </button>
</div>